import React, { useState, useMemo, useEffect } from 'react'
import { PartnerService } from '@duck/api-definitions'
import { OrganizationTableFilters } from './OrganizationTableFilters'
import { OrganizationsTable } from './OrganizationsTable'
import { OrganizationDialog } from './OrganizationDialog'
import { baseColumns, type Column } from './utils'
import { useTar } from '@/modules'
import { useOrganizations, useInvalidateOrganizations } from '@/storeHooks/useOrganizations'

const STORAGE_KEY = 'admin-organization-filters'

interface FilterState {
    sortField: keyof PartnerService.Organization
    sortDirection: 'asc' | 'desc'
    filterText: string
    typeFilter: PartnerService.OrganizationType[]
    activeFilter: boolean | null
    visibleColumnKeys: string[]
}

// Utility functions for localStorage
const loadFiltersFromStorage = (): FilterState | null => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        return stored ? JSON.parse(stored) : null
    } catch {
        return null
    }
}

const saveFiltersToStorage = (filters: FilterState): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
    } catch {
        // Ignore localStorage errors
    }
}

const reconstructColumns = (visibleKeys?: string[]): Column[] => {
    const keys = visibleKeys || baseColumns.filter(col => col.visible).map(col => col.key)
    return baseColumns.map(col => ({
        ...col,
        visible: keys.includes(col.key)
    }))
}

export const OrganizationsManagement: React.FC = () => {
    const { t } = useTar()
    // Initialize state from localStorage or defaults
    const storedFilters = loadFiltersFromStorage()

    const [sortField, setSortField] = useState<keyof PartnerService.Organization>(
        storedFilters?.sortField ?? 'name'
    )
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
        storedFilters?.sortDirection ?? 'asc'
    )
    const [filterText, setFilterText] = useState(storedFilters?.filterText ?? '')
    const [typeFilter, setTypeFilter] = useState<PartnerService.OrganizationType[]>(
        Array.isArray(storedFilters?.typeFilter)
            ? (storedFilters?.typeFilter as PartnerService.OrganizationType[])
            : []
    )
    const [activeFilter, setActiveFilter] = useState<boolean | null>(
        storedFilters?.activeFilter !== undefined ? storedFilters?.activeFilter : null
    )
    const [columnDropdownOpen, setColumnDropdownOpen] = useState(false)
    const [columns, setColumns] = useState<Column[]>(
        () => reconstructColumns(storedFilters?.visibleColumnKeys)
    )

    // Use React Query hook for organizations
    const { organizations, isLoading, isError, error: queryError } = useOrganizations()
    const { invalidateLists } = useInvalidateOrganizations()

    // Dialog state
    const [isOrganizationDialogOpen, setIsOrganizationDialogOpen] = useState(false)
    const [selectedOrganization, setSelectedOrganization] = useState<PartnerService.Organization | null>(null)

    // Derive loading and error states from query
    const loading = isLoading
    const error = isError ? (queryError ? 'Failed to load organizations. Please try again.' : null) : null

    // Save to localStorage whenever filter state changes
    useEffect(() => {
        const visibleColumnKeys = columns.filter(col => col.visible).map(col => col.key)
        const filterState: FilterState = {
            sortField,
            sortDirection,
            filterText,
            typeFilter,
            activeFilter,
            visibleColumnKeys,
        }
        saveFiltersToStorage(filterState)
    }, [sortField, sortDirection, filterText, typeFilter, activeFilter, columns])

    const filteredAndSortedOrganizations = useMemo(() => {
        let filtered = organizations

        // Text filter
        if (filterText) {
            const searchTerm = filterText.toLowerCase()
            filtered = filtered.filter(organization =>
                organization.name.toLowerCase().includes(searchTerm) ||
                (organization.description && organization.description.toLowerCase().includes(searchTerm))
            )
        }

        // Type filter
        if (typeFilter.length > 0) {
            const typeSet = new Set(typeFilter)
            filtered = filtered.filter(organization => typeSet.has(organization.type))
        }

        // Active filter
        if (activeFilter !== null) {
            filtered = filtered.filter(organization => organization.active === activeFilter)
        }

        // Sort
        const sorted = [...filtered].sort((a, b) => {
            const aValue = a[sortField]
            const bValue = b[sortField]

            if (aValue === null || aValue === undefined) return 1
            if (bValue === null || bValue === undefined) return -1

            let comparison = 0
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                comparison = aValue.localeCompare(bValue)
            } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
                comparison = aValue === bValue ? 0 : aValue ? 1 : -1
            } else if (typeof aValue === 'number' && typeof bValue === 'number') {
                comparison = aValue - bValue
            } else {
                comparison = String(aValue).localeCompare(String(bValue))
            }

            return sortDirection === 'asc' ? comparison : -comparison
        })

        return sorted
    }, [organizations, filterText, typeFilter, activeFilter, sortField, sortDirection])

    const handleSort = (field: keyof PartnerService.Organization) => {
        if (field === sortField) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    const toggleColumnVisibility = (columnKey: string) => {
        setColumns(prev => prev.map(col =>
            col.key === columnKey
                ? { ...col, visible: !col.visible }
                : col
        ))
    }

    const handleCreateNewOrganization = () => {
        setSelectedOrganization(null)
        setIsOrganizationDialogOpen(true)
    }

    const handleOrganizationClick = (organization: PartnerService.Organization) => {
        setSelectedOrganization(organization)
        setIsOrganizationDialogOpen(true)
    }

    const handleCloseDialog = () => {
        setIsOrganizationDialogOpen(false)
        setSelectedOrganization(null)
    }

    const handleOrganizationSaved = () => {
        invalidateLists()
    }

    const visibleColumns = columns.filter(col => col.visible)

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold leading-7 text-foreground sm:truncate sm:text-3xl sm:tracking-tight">
                        {t('organizations.organizationManagement')}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {t('organizations.organizationManagementDescription')}
                    </p>
                </div>
                <button
                    onClick={handleCreateNewOrganization}
                    className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors flex items-center gap-2"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('organizations.createOrganization')}
                </button>
            </div>

            {/* Filters and Controls */}
            <OrganizationTableFilters
                filterText={filterText}
                setFilterText={setFilterText}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                activeFilter={activeFilter}
                setActiveFilter={setActiveFilter}
                columns={columns}
                columnDropdownOpen={columnDropdownOpen}
                setColumnDropdownOpen={setColumnDropdownOpen}
                toggleColumnVisibility={toggleColumnVisibility}
            />

            {/* Error State */}
            {error && (
                <div className="rounded-md bg-error/10 border border-error/20 p-4">
                    <div className="text-error text-sm">{error}</div>
                </div>
            )}

            {/* Organizations Table */}
            {!error && (
                <OrganizationsTable
                    organizations={filteredAndSortedOrganizations}
                    totalOrganizations={organizations.length}
                    visibleColumns={visibleColumns}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    onOrganizationClick={handleOrganizationClick}
                    loadingMode={loading}
                />
            )}

            {/* Organization Dialog */}
            <OrganizationDialog
                isOpen={isOrganizationDialogOpen}
                onClose={handleCloseDialog}
                organization={selectedOrganization}
                onOrganizationSaved={handleOrganizationSaved}
            />
        </div>
    )
}

